/**
 * Convert a 6-digit hex (`#RRGGBB`) to a Tailwind-style HSL string
 * (`H S% L%`) suitable for assigning directly to a CSS variable used as
 * `hsl(var(--name))`.
 *
 * The pre-hydration script in `src/app/layout.tsx` inlines an equivalent
 * implementation (it can't import; it has to run before React mounts).
 * Keep both in sync — a rounding tweak here without an equivalent edit
 * to the inline script will cause an accent flash on reload.
 */
export const hexToHsl = (hex: string): string => {
  if (!hex) return '0 0% 0%';
  const stripped = hex.replace('#', '');
  const r = parseInt(stripped.substring(0, 2), 16) / 255;
  const g = parseInt(stripped.substring(2, 4), 16) / 255;
  const b = parseInt(stripped.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `${(h * 360).toFixed(0)} ${(s * 100).toFixed(0)}% ${(l * 100).toFixed(0)}%`;
};

/**
 * A `#RRGGBB` colour as unrounded HSL parts: hue in degrees, saturation and
 * lightness in percent. `hexToHsl` rounds to whole numbers, which is right for
 * the accent (the pre-hydration script repeats that rounding) but moves a
 * colour by a step; a seller's page background is painted from these instead,
 * so the colour on the page is the colour they picked.
 */
export const hexToHslParts = (hex: string): [number, number, number] => {
  const stripped = hex.replace('#', '');
  const r = parseInt(stripped.substring(0, 2), 16) / 255;
  const g = parseInt(stripped.substring(2, 4), 16) / 255;
  const b = parseInt(stripped.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case r: h = (g - b) / d + (g < b ? 6 : 0); break;
    case g: h = (b - r) / d + 2; break;
    default: h = (r - g) / d + 4; break;
  }
  return [h * 60, s * 100, l * 100];
};

/**
 * Validate a stored colour as a 6-digit hex and normalise it to lowercase, or
 * return null. Validation only: no contrast clamp.
 *
 * Every seller-chosen colour is read from a document a client wrote, so it is
 * untrusted until it matches this, and it has to match before it can reach a
 * CSS custom property or an inline style. The accent helpers below apply the
 * same test before they clamp; the gradient colour stops here, because it is a
 * faint background tint with nothing painted on it (see DesignOverride in
 * src/lib/product-design.ts).
 */
export const parseHex6 = (hex: unknown): string | null =>
  typeof hex === 'string' && /^#[0-9a-fA-F]{6}$/.test(hex) ? hex.toLowerCase() : null;

/**
 * Convert `H S% L%` back to a `#RRGGBB` hex string. The inverse of
 * `hexToHsl`, used by the accent clamp below so a clamped colour can be handed
 * back to an `<input type="color">` (which only speaks hex).
 */
export const hslToHex = (h: number, s: number, l: number): string => {
  const sN = s / 100;
  const lN = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sN * Math.min(lN, 1 - lN);
  const f = (n: number) => {
    const v = lN - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(255 * v).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/** sRGB channel -> linear, per WCAG 2.x relative-luminance. */
const toLinear = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

/** WCAG relative luminance of a `#RRGGBB` colour. */
export const relativeLuminance = (hex: string): number => {
  const s = hex.replace('#', '');
  const r = toLinear(parseInt(s.substring(0, 2), 16) / 255);
  const g = toLinear(parseInt(s.substring(2, 4), 16) / 255);
  const b = toLinear(parseInt(s.substring(4, 6), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two `#RRGGBB` colours. Always >= 1. */
export const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * The near-white label a SELLER's accent carries (`--primary-foreground` on
 * their page, written by ThemeProvider through accentLabelHex below). The
 * clamp below is expressed as a contrast requirement against this exact colour
 * rather than as a bare lightness ceiling, so every clamped accent keeps it.
 */
export const PRIMARY_FOREGROUND_HEX = '#fafafa';

/** WCAG AA for normal text. A button label is normal-sized text on `--primary`. */
const MIN_ACCENT_CONTRAST = 4.5;

/** The dark label, the light theme's text colour (`--foreground`). */
const ACCENT_INK_HEX = '#09090b';

/**
 * The label for text set ON an accent (`--primary-foreground`): the buy
 * button's words, a count badge's number.
 *
 * A seller's accent is clamped to carry the near-white label, and keeps it
 * here unchanged. The brand defaults are not clamped: the light one carries
 * pure white (4.67:1, where near-white is 4.47:1), and the dark one is a light
 * tone that takes the dark label (6.22:1). So the rule is the near-white label
 * when it reads, else pure white, else ink.
 */
export const accentLabelHex = (hex: unknown): string => {
  const normalized = parseHex6(hex);
  if (!normalized) return PRIMARY_FOREGROUND_HEX;
  if (contrastRatio(normalized, PRIMARY_FOREGROUND_HEX) >= MIN_ACCENT_CONTRAST) return PRIMARY_FOREGROUND_HEX;
  if (contrastRatio(normalized, '#ffffff') >= MIN_ACCENT_CONTRAST) return '#ffffff';
  return ACCENT_INK_HEX;
};

/**
 * Floor on lightness. Below this an accent stops reading as a colour and
 * becomes indistinguishable from the dark surface it sits on, so a hue that
 * cannot reach MIN_ACCENT_CONTRAST above this floor is darkened to the floor
 * and no further. In practice only a fully saturated yellow/cyan gets here.
 */
const MIN_ACCENT_LIGHTNESS = 20;

/**
 * Clamp a seller-chosen accent so `--primary-foreground` stays legible on it.
 *
 * WHY THIS EXISTS. A seller picks their page's accent and BUYERS live with the
 * result, on the page where the price and the buy button are. (There used to
 * be a per-visitor accent too, a choice about one's own screen, left
 * unclamped; it was retired on 2026-10-21.) So the seller's accent is clamped,
 * and it is clamped here rather than at the seller's colour picker so that the
 * buyer's page and the seller's preview cannot disagree about what a given hex
 * renders as.
 *
 * Only LIGHTNESS moves. Hue and saturation are the identity of the colour the
 * seller chose; darkening preserves both while buying contrast, whereas
 * desaturating would hand back a colour they did not pick.
 *
 * Returns the input unchanged when it already passes, and returns null for
 * anything that is not a 6-digit hex so callers can fall through to no
 * accent.
 */
/**
 * The WORST surface an accent is painted on when it is used as TEXT, per theme.
 *
 * Not `--background`. Accent ink lands on panels as often as on the page, and
 * which panel is hardest flips with the theme, because contrast depends on the
 * gap between ink and surface and the ink changes direction:
 *
 *   dark  — the ink is light, so the LIGHTEST surface is the hard one.
 *           `--muted` (240 4% 12%) sits above `--card` (8%) and `--background`
 *           (3%), so clearing muted clears all three.
 *   light — the ink is dark, so the DARKEST surface is the hard one.
 *           The hover tone `--accent` (240 5% 91%, a hovered or highlighted
 *           row) sits below `--background` (96%), `--muted` (95.9%) and the
 *           white `--card`, so clearing it clears them all. (Until 2026-10-21
 *           the card itself was that 91% grey; the cards went white and the
 *           hover tone took over its value.)
 *
 * Targeting `--background` instead was measured leaving the real surfaces
 * short: colours cleared 4.5 against the dark page and still landed at 4.16 on
 * a card, which is where the product page actually draws its tag chips.
 *
 * Keep in step with src/app/globals.css.
 */
const SURFACE_HEX = { light: '#e7e7e9', dark: '#1d1d20' } as const;

/**
 * Make an accent legible as TEXT on the current theme's surface.
 *
 * WHY THIS IS SEPARATE FROM clampAccentHex. That function solves one problem:
 * `--primary-foreground` (near-white) is painted on top of the accent, so the
 * accent has to be dark enough for white to read on it. But `--primary` has a
 * second job nothing was checking. It is also used AS the ink (on a seller's
 * page the tag chips and links are `--link` text, which is this accent there),
 * and the two jobs pull in opposite directions on a dark page.
 *
 * Measured before this existed: every clamped accent landed between 3.2 and
 * 4.0 against the dark surface, under the 4.5 AA needs for normal text. Worse,
 * the clamp made bright picks actively worse on purpose, because darkening is
 * exactly what buys contrast for the white-on-accent case: a seller's yellow
 * went from 17.3 against the dark page to 3.8.
 *
 * So the accent keeps its clamped value where it is a SURFACE, and this
 * produces the value to use where it is INK. Only lightness moves, as in
 * clampAccentHex, so the hue and saturation the seller chose survive: on a dark
 * page it walks lightness up, on a light page down.
 *
 * Returns the input unchanged when it already passes, and null for anything
 * that is not a 6-digit hex, so callers can fall through exactly as they do
 * with clampAccentHex.
 *
 * `surfaces` replaces the theme's own worst surface on a page that paints its
 * own colours: a seller's background colour (src/lib/page-background.ts), whose
 * page, cards and panels are all shades of that colour. The ink has to clear
 * every one of them, so the walk tests the lowest contrast among them.
 */
export const readableAccentHex = (
  hex: unknown,
  isDark: boolean,
  surfaces?: readonly string[],
): string | null => {
  const normalized = parseHex6(hex);
  if (!normalized) return null;
  const against = surfaces && surfaces.length > 0 ? surfaces : [isDark ? SURFACE_HEX.dark : SURFACE_HEX.light];
  const worst = (ink: string) => Math.min(...against.map((surface) => contrastRatio(ink, surface)));
  if (worst(normalized) >= MIN_ACCENT_CONTRAST) return normalized;

  const [h, s, l] = hexToHsl(normalized).split(' ').map(parseFloat);
  // On a dark surface legibility is bought by getting LIGHTER, on a light
  // surface by getting darker. Walk one percent at a time, bounded at each end,
  // so this terminates for every hue including the ones that never pass.
  const step = isDark ? 1 : -1;
  const limit = isDark ? 100 : 0;
  for (let next = Math.round(l) + step; isDark ? next <= limit : next >= limit; next += step) {
    const candidate = hslToHex(h, s, next);
    if (worst(candidate) >= MIN_ACCENT_CONTRAST) return candidate;
  }
  // Unreachable in practice: pure white clears AA on the dark surface and pure
  // black clears it on the light one, so the walk always finds something first.
  return hslToHex(h, s, limit);
};

export const clampAccentHex = (hex: unknown): string | null => {
  const normalized = parseHex6(hex);
  if (!normalized) return null;
  if (contrastRatio(normalized, PRIMARY_FOREGROUND_HEX) >= MIN_ACCENT_CONTRAST) {
    return normalized;
  }
  const [h, s, l] = hexToHsl(normalized).split(' ').map(parseFloat);
  // Walk lightness down a percent at a time. Bounded by the floor, so this
  // terminates for every hue including the ones that can never pass.
  for (let next = Math.floor(l) - 1; next >= MIN_ACCENT_LIGHTNESS; next--) {
    const candidate = hslToHex(h, s, next);
    if (contrastRatio(candidate, PRIMARY_FOREGROUND_HEX) >= MIN_ACCENT_CONTRAST) {
      return candidate;
    }
  }
  return hslToHex(h, s, MIN_ACCENT_LIGHTNESS);
};

/** The theme pages' own colours (`--background` in src/app/globals.css). */
export const PAGE_HEX = { light: '#f4f4f5', dark: '#070708' } as const;

/** WCAG 1.4.11: the shape of a control needs 3:1 against what surrounds it. */
const MIN_FILL_CONTRAST = 3;

/**
 * Move a clamped accent until a button FILLED with it can be seen on the page.
 *
 * WHY THIS IS SEPARATE FROM clampAccentHex. That one guarantees the white label
 * reads ON the accent (4.5:1). It says nothing about the button against the
 * page, so a near-black or deep navy accent passed straight through and drew a
 * buy button that vanished into a dark page (1.21:1 measured in the 2026-10-20
 * audit), and ticked checkboxes and selected chips with it.
 *
 * Only lightness moves, as in the two functions above, so the seller's hue and
 * saturation survive. The walk tries a percent lighter and a percent darker at
 * each step, so the first colour that works is the smallest change: lighter on
 * the dark page, darker on a mid-tone seller background that only black clears.
 * It never takes a colour under which the white label would drop below 4.5:1:
 * the label is the part that has to be read, so when the two cannot both be
 * met (a dark seller background colour can leave no room) the label wins and
 * the fill keeps the most contrast it can get. On the light page nothing moves
 * in practice, since an accent dark enough for a white label is already more
 * than 3:1 from it, and the brand accents pass unchanged (the light default
 * #625ff7 at 4.25:1 on the light page, the dark default #8481ff at 6.29:1 on
 * the dark page).
 *
 * Returns the input when it already passes, and null for anything that is not
 * a 6-digit hex.
 */
export const visibleAccentHex = (hex: unknown, pageHex: unknown): string | null => {
  const normalized = parseHex6(hex);
  if (!normalized) return null;
  const page = parseHex6(pageHex);
  if (!page) return normalized;
  let best = normalized;
  let bestContrast = contrastRatio(normalized, page);
  if (bestContrast >= MIN_FILL_CONTRAST) return normalized;
  const [h, s, l] = hexToHsl(normalized).split(' ').map(parseFloat);
  const start = Math.round(l);
  for (let distance = 1; distance <= 100; distance++) {
    for (const next of [start + distance, start - distance]) {
      if (next < 0 || next > 100) continue;
      const candidate = hslToHex(h, s, next);
      if (contrastRatio(candidate, PRIMARY_FOREGROUND_HEX) < MIN_ACCENT_CONTRAST) continue;
      const against = contrastRatio(candidate, page);
      if (against >= MIN_FILL_CONTRAST) return candidate;
      if (against > bestContrast) {
        best = candidate;
        bestContrast = against;
      }
    }
  }
  return best;
};
